import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// import { useNavigate } from 'react-router-dom';

import { ReactComponent as TransactionIcon } from '../../assets/icons/sidebar/sidebar-payment.svg';
import { ReactComponent as SlashIcon } from '../../assets/icons/slash.svg';
import { ReactComponent as StopIcon } from '../../assets/icons/stop-circle.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import { PersonalSessionInterface } from '../../interface/charging-session.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import {
  disablePersonalSessionFiltersApplied,
  fetchAllPersonalSessions,
  fetchListPersonalSessionFilters,
  updatePersonalSessionFilters,
  setPersonalSessionSortData,
} from '../../redux/slices/PersonalChargingSessionSlice';
import CustomActionBar from '../../shared-components/custom-action-bar/CustomActionBar';
import CustomRadioButton from '../../shared-components/custom-radio-button/CustomRadioButton';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import DeviceSidePanel from '../../shared-components/table-side-panel/DeviceSidePanel';
import EndConsumerSidePanel from '../../shared-components/table-side-panel/EndConsumerSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { getDaysBetween } from '../../shared-functions/DateTime';
import occpEvent from '../../utils/api/ocpp-events';

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element | null;
  rightCorner: string;
  notification: boolean;
}

/**
 * Personal Session Table Component
 */
function PersonalSessionTable({
  currentPage,
  setCurrentPage,
}: {
  currentPage: string;
  setCurrentPage: (value: string) => void;
}) {
  const { t } = useTranslation('transaction');
  const dispatch = useAppDispatch();
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    content: null,
    header: true,
    rightCorner: '',
    notification: false,
  });
  // @ts-ignore
  const {
    payments: sessions,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
    // @ts-ignore
  } = useAppSelector((state) => state.personalChargingSession);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  // const [isPolling, setIsPolling] = useState<boolean>(false);
  //   const navigate = useNavigate();
  // const { deviceDetails } = useAppSelector((state) => state.tableSidePanel);

  /**
   * Function to handle radio button change
   */
  const handleAvailabilityChange = (name: string, value: string) => {
    setCurrentPage(value);
  };

  /**
   * Modal box close function
   */
  const showSuccessNotification = (message: string) => {
    if (message) {
      setOpenStatus(true);
      setModalView({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
            }}
          />
        ),
        rightCorner: '',
        notification: true,
      });
    } else {
      setOpenStatus(false);
    }
  };

  // /**
  //  * Function to open device view model
  //  */
  // const handleOpenModelView = (_: any, newTab: boolean) => {
  //     if (deviceDetails) {
  //         dispatch(
  //             setSelectedDevice({
  //                 id: deviceDetails?.id,
  //                 name: deviceDetails?.chargingPointName,
  //                 chargingPointSn: deviceDetails?.chargingPointSn,
  //                 modelSeries: deviceDetails?.modelSeries,
  //             }),
  //         );

  //         if (newTab) {
  //             const url = `/ev-admin/device-management/device/${deviceDetails.id}/device-details?searchBy=${deviceDetails.chargingPointName}`;
  //             window.open(url, '_blank'); // opens in a new tab
  //         } else {
  //             navigate({
  //                 pathname: `../device-management/device/${deviceDetails.id}/device-details`,
  //                 search: `?searchBy=${deviceDetails.chargingPointName}`,
  //             });
  //         }
  //     } else {
  //         console.log('Error while opening device in separate page');
  //     }
  // };
  /**
   * Fetch the session details in redux
   */
  const fetchSessions = (data: any) => {
    if (getDaysBetween(data.startDate, data.endDate) > 30) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading) {
      dispatch(fetchAllPersonalSessions({ ...data, deviceType: 'personal' }));
    }
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions({ ...queryParameters, page, limit, ...sortValue });
  };
  /**
   * Function to handle stop button click
   * @param data
   */
  const handleStop = (data: PersonalSessionInterface | string[]) => {
    let itemsToProcess: PersonalSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = sessions.filter((session) => data.includes(session.id));
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const promises = itemsToProcess.map((item) => {
      const payload = {
        chargingPointSn: item.chargingPointSn,
        transactionId: item.transactionId,
        userCustomId: item.userCustomId,
        forceStopTransaction: false,
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsStopRequestSuccess')
            : t('transactionStopRequestSuccess'),
        );
        handleRefresh();
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Function to handle force stop button click
   * @param data
   */
  const handleForceStop = (data: PersonalSessionInterface | string[]) => {
    let itemsToProcess: PersonalSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = sessions.filter((session) => data.includes(session.id));
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const promises = itemsToProcess.map((item) => {
      const payload = {
        chargingPointSn: item.chargingPointSn,
        transactionId: item.transactionId,
        userCustomId: item.userCustomId,
        forceStopTransaction: true,
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsForceStopRequestSuccess')
            : t('transactionForceStopRequestSuccess'),
        );
        handleRefresh();
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };
  const column = [
    {
      label: t('chargingStartedAt'),
      accessorKey: 'transactionStartsAt' as keyof PersonalSessionInterface,
      sortAllow: true,
      /**
       * Custom data for the charging started at column
       * @param rowData
       * @returns
       */
      customData: (rowData: PersonalSessionInterface) => (
        <span>{ConvertUTCtoDate(rowData?.transactionStartsAt as string | any)}</span>
      ),
    },
    {
      label: t('chargingEndedAt'),
      accessorKey: 'transactionEndsAt' as keyof PersonalSessionInterface,
      sortAllow: true,
      /**
       * Custom data for the charging ended at column
       * @param rowData
       * @returns
       */
      customData: (rowData: PersonalSessionInterface) => (
        <span>{ConvertUTCtoDate(rowData?.transactionEndsAt as string | any)}</span>
      ),
    },
    {
      label: t('transactionId'),
      accessorKey: 'transactionId' as keyof PersonalSessionInterface,
    },
    {
      label: t('endConsumerId'),
      accessorKey: 'userCustomId' as keyof PersonalSessionInterface,
      queryParamsKey: 'userCustomId',
      queryParamsValue: 'userCustomId',
      sidePanelComponent: <EndConsumerSidePanel fetchIdFrom="queryParams" />,
      /**
       * isSidePanelDisabled
       * @param row
       * @returns
       */
      isSidePanelDisabled: (row: any) => row.isGuestUser,
    },
    {
      label: t('endConsumerName'),
      accessorKey: 'endConsumer' as keyof PersonalSessionInterface,
      queryParamsKey: 'userCustomId',
      queryParamsValue: 'userCustomId',
      sidePanelComponent: <EndConsumerSidePanel fetchIdFrom="queryParams" />,
      /**
       * isSidePanelDisabled
       * @param row
       * @returns
       */
      isSidePanelDisabled: (row: any) => row.isGuestUser,
    },

    {
      label: t('chargerName'),
      accessorKey: 'chargingPointName' as keyof PersonalSessionInterface,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: <DeviceSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('chargerSN'),
      accessorKey: 'chargingPointSn' as keyof PersonalSessionInterface,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: <DeviceSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof PersonalSessionInterface,
      // sortAllow: true,
      /**
       * Custom data for the status column
       * @param rowData
       * @returns
       */
      customData: (rowData: PersonalSessionInterface) => (
        <span className="capitalize">{rowData?.status}</span>
      ),
    },
    {
      label: t('actions'),
      accessorKey: 'id' as keyof PersonalSessionInterface,
      /**
       * Custom data for the actions column
       * @param rowData
       * @returns
       */
      customData: (rowData: PersonalSessionInterface) => {
        if (rowData?.status === 'inProgress') {
          return (
            <div className="stop-buttons">
              <span role="presentation">
                <StopIcon onClick={() => handleStop(rowData)} />
              </span>
              <span role="presentation">
                <SlashIcon onClick={() => handleForceStop(rowData)} />
              </span>
            </div>
          );
        }
        return null;
      },
    },
  ];

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions({ ...value, ...queryParameters, ...sortValue });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setPersonalSessionSortData(sortData));
    fetchSessions({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Poll session data every 10 seconds
   */
  // useEffect(() => {
  //   let timeout: NodeJS.Timeout;
  //   if (isPolling) {
  //     timeout = setTimeout(() => {
  //       handleRefresh();
  //       setIsPolling(false);
  //     }, 10000);
  //   }
  //   return () => clearTimeout(timeout);
  // }, [isPolling]);
  useEffect(() => {
    if (isFilterUpdated || sessions.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchSessions({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disablePersonalSessionFiltersApplied());
    }
  }, [filters]);

  useEffect(() => {
    dispatch(
      setPageTitle({ title: t('sessions'), icon: <TransactionIcon />, count: totalResults }),
    );
  }, [totalResults]);

  return (
    <div className="payment-management-wrap">
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <div className="table-content">
        <CustomTable
          dataKey="PersonalSessions"
          version="v2"
          header={column}
          height={window.innerHeight >= 700 ? 337 : 342}
          data={sessions}
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updatePersonalSessionFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListPersonalSessionFilters}
          hidedColumnNames={[
            'sessionAmount',
            'platformAmount',
            'networkRevenue',
            'sharableRevenue',
          ]}
          rightTopContent={
            <div className="revenue-management-toggle-wrap">
              <CustomRadioButton
                name=""
                value="commercial"
                label={t('commercialCharger')}
                selected={currentPage === 'commercial'}
                onChangeFunc={handleAvailabilityChange}
              />
              <CustomRadioButton
                name=""
                value="personal"
                label={t('personalCharger')}
                selected={currentPage === 'personal'}
                onChangeFunc={handleAvailabilityChange}
              />
            </div>
          }
          refreshRequired
          handleRefresh={handleRefresh}
          ReferenceCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          checkbox
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          selectKey="id"
          isRowSelectable={(row) => row.status === 'inProgress'}
        />
        <CustomActionBar
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          actions={[
            {
              label: t('stop'),
              // icon: <StopIcon />,
              buttonType: 'danger-outline',
              /**
               * handle stop
               * @todo
               */
              onClick: () => handleStop(selectedRows),
            },
            {
              label: t('forceStop'),
              // icon: <SlashIcon />,
              buttonType: 'error',
              /**
               * handle force stop
               * @todo
               */
              onClick: () => handleForceStop(selectedRows),
            },
          ]}
        />
      </div>
    </div>
  );
}

export default PersonalSessionTable;
